import { PageIndices, TabIndices } from '../common/i-doc/i-doc.registry.ts';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { RoleProtectedRoute } from '../components/RoleProtectedRoute';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Paper,
  Snackbar,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import { AdaptiveTabs } from '../components/common/AdaptiveTabs';
import ClearIcon from '@mui/icons-material/Clear';
import { searchExperts, getSpecializations, getMyExperts } from '../services/expert.service';
import { requestConnection } from '../services/user-connections.service';
import { ExpertListItemDto, PaginatedExpertsResponse, SearchExpertsParams, Specialization } from '../types/expert';
import { ExpertCard } from '../components/experts/ExpertCard';
import { UniversalPagination } from '../components/common/UniversalPagination';
import { ConsultationRequestModal } from '../components/experts/ConsultationRequestModal';
import { ConnectionPermissionsDialog } from '../components/experts/ConnectionPermissionsDialog';
import { ConnectionSuccessDialog } from '../components/experts/ConnectionSuccessDialog';
import { ConnectionAccessItem } from '../types/access-control.types';
import { useAppDispatch } from '../store';
import { createConversation } from '../store/chat/chatSlice';
import { PageHint } from '@/components/common/PageHint';

const EXPERT_FILTERS_KEY = 'expertFilters';

const FindExpertsTabContent: React.FC = () => {
  const [expertsResponse, setExpertsResponse] = useState<PaginatedExpertsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useState<SearchExpertsParams>(() => {
    const savedFilters = localStorage.getItem(EXPERT_FILTERS_KEY);
    const initialSearch = savedFilters ? JSON.parse(savedFilters).search : '';
    return { page: 1, limit: 12, search: initialSearch };
  });

  const [specializations, setSpecializations] = useState<Specialization[]>([]);
  const [selectedSpecializations, setSelectedSpecializations] = useState<string[]>(() => {
    const savedFilters = localStorage.getItem(EXPERT_FILTERS_KEY);
    return savedFilters ? JSON.parse(savedFilters).specializations : [];
  });

  const [expertForRequest, setExpertForRequest] = useState<ExpertListItemDto | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [expertForConnection, setExpertForConnection] = useState<ExpertListItemDto | null>(null);
  const [connectionSuccessExpert, setConnectionSuccessExpert] = useState<ExpertListItemDto | null>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isInitialLoad) {
      localStorage.setItem(EXPERT_FILTERS_KEY, JSON.stringify({
        search: searchParams.search || '',
        specializations: selectedSpecializations
      }));
    }
  }, [searchParams.search, selectedSpecializations, isInitialLoad]);

  const fetchSpecializations = useCallback(async () => {
    try {
      const specs = await getSpecializations();
      setSpecializations(specs);

      const savedFilters = localStorage.getItem(EXPERT_FILTERS_KEY);
      if (!savedFilters) {
        setSelectedSpecializations(specs.map(s => s.code));
      }
    } catch (err) {
      console.error('Failed to fetch specializations', err);
      setError('Не вдалося завантажити список спеціалізацій.');
    } finally {
      setIsInitialLoad(false);
    }
  }, []);

  useEffect(() => {
    fetchSpecializations();
  }, [fetchSpecializations]);

  const fetchExperts = useCallback(async () => {
    if (isInitialLoad) return; 

    try {
      setLoading(true);
      setError(null);
      const params: SearchExpertsParams = { ...searchParams };

      const isFilterApplicable = selectedSpecializations.length > 0 && selectedSpecializations.length < specializations.length;
      if (isFilterApplicable) {
        params.specializations = selectedSpecializations;
      }

      const response = await searchExperts(params);
      setExpertsResponse(response);
    } catch (err) {
      const errorMessage = 'Не вдалося завантажити список експертів.';
      setError(errorMessage);
      console.error(errorMessage, err);
    } finally {
      setLoading(false);
    }
  }, [searchParams, selectedSpecializations, specializations.length, isInitialLoad]);

  useEffect(() => {
    fetchExperts();
  }, [fetchExperts]);

  const handleRequestConsultation = (expert: ExpertListItemDto) => {
    setExpertForRequest(expert);
  };

  const handleStartChat = async (expert: ExpertListItemDto) => {
    try {
      await dispatch(createConversation({ targetUserId: expert.id })).unwrap();
      navigate('/chat');
    } catch (err: any) {
        const errorMessage = err || 'Не вдалося почати чат. Спробуйте ще раз.';
        setError(errorMessage);
        console.error('Failed to start chat:', err);
    }
  };

  const handlePageChange = (value: number) => {
    setSearchParams(prev => ({ ...prev, page: value }));
  };

  const handleLimitChange = (value: number) => {
    setSearchParams(prev => ({ ...prev, limit: value, page: 1 }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setSearchParams(prev => ({ ...prev, [name]: value, page: 1 }));
  };

  const handleClearSearch = () => {
    setSearchParams(prev => ({ ...prev, search: '', page: 1 }));
  };

  const handleSpecializationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setSelectedSpecializations(prev => {
      if (checked) {
        return [...prev, name];
      } else {
        return prev.filter(spec => spec !== name);
      }
    });
    setSearchParams(prev => ({ ...prev, page: 1 }));
  };

  const handleRequestConnection = (expert: ExpertListItemDto) => {
    setExpertForConnection(expert);
  };

  const handleConnectionPermissionsConfirm = async (access: ConnectionAccessItem[], comment: string) => {
    if (!expertForConnection) return;
    const expert = expertForConnection;
    try {
      await requestConnection(expert.id, access, comment);
      setExpertsResponse(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          data: prev.data.map(e =>
            e.id === expert.id ? { ...e, interactionStatus: 'pending' } : e
          ),
        };
      });
      setConnectionSuccessExpert(expert);
    } catch (err: any) {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Не вдалося надіслати заявку.');
    } finally {
      setExpertForConnection(null);
    }
  };

  const handleCloseRequestModal = (isSuccess: boolean) => {
    setExpertForRequest(null);
    if (isSuccess) {
      setSuccessMessage('Ваш запис успішно створено! Очікуйте на підтвердження від експерта.');
    }
  };

  const renderContent = () => {
    if (!expertsResponse) {
        if (loading) {
            return <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
        }
        if (error) {
            return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
        }
        return <Typography sx={{ mt: 4, textAlign: 'center' }}>Експертів не знайдено.</Typography>;
    }

    if (expertsResponse.data.length === 0) {
        return <Typography sx={{ mt: 4, textAlign: 'center' }}>Експертів не знайдено.</Typography>;
    }

    return (
      <>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {expertsResponse.data.map(expert => (
            <Grid item key={expert.id} xs={12} sm={12} md={6} lg={4}>
              <ExpertCard
                expert={expert}
                onRequestConsultation={handleRequestConsultation}
                onRequestConnection={handleRequestConnection}
                onStartChat={handleStartChat}
                showChatButton={true}
              />
            </Grid>
          ))}
        </Grid>
        <UniversalPagination
          page={expertsResponse.pagination.currentPage}
          count={expertsResponse.pagination.pages}
          limit={searchParams.limit ?? 12}
          limitOptions={[12, 24, 48]}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          storageKey="pagination:experts"
          sx={{ mt: 4 }}
        />
      </>
    );
  };

  return (
    <Box>
        <PageHint iDoc={PageIndices.EXPERTS} />
        
        <Paper sx={{ p: 2, mt: 2 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                    <TextField 
                        fullWidth
                        label="Пошук за іменем або ключовими словами"
                        name="search"
                        value={searchParams.search || ''}
                        onChange={handleSearchChange}
                        variant={searchParams.search ? "filled" : "outlined"}
                        color={searchParams.search ? "primary" : undefined}
                        InputProps={{
                          endAdornment: searchParams.search ? (
                            <InputAdornment position="end">
                              <IconButton onClick={handleClearSearch} edge="end">
                                <ClearIcon />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Спеціалізації</Typography>
                  <FormGroup row>
                    {specializations.map(spec => (
                      <FormControlLabel
                        key={spec.code}
                        control={
                          <Checkbox
                            checked={selectedSpecializations.includes(spec.code)}
                            onChange={handleSpecializationChange}
                            name={spec.code}
                          />
                        }
                        label={spec.name}
                      />
                    ))}
                  </FormGroup>
                </Grid>
            </Grid>
        </Paper>

        <Box sx={{ position: 'relative', mt: 2 }}>
            {loading && (
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.5)', zIndex: 1 }}>
                    <CircularProgress />
                </Box>
            )}
            {renderContent()}
        </Box>

        {expertForRequest && (
            <ConsultationRequestModal
                expert={expertForRequest}
                open={!!expertForRequest}
                onClose={handleCloseRequestModal}
            />
        )}
        {expertForConnection && (
            <ConnectionPermissionsDialog
                open={!!expertForConnection}
                onClose={() => setExpertForConnection(null)}
                expertId={expertForConnection.id}
                expertName={expertForConnection.name}
                mode="new"
                onConfirm={handleConnectionPermissionsConfirm}
            />
        )}
        {connectionSuccessExpert && (
            <ConnectionSuccessDialog
                open={!!connectionSuccessExpert}
                expertName={connectionSuccessExpert.name}
                onClose={() => setConnectionSuccessExpert(null)}
                onBookAppointment={() => {
                    setConnectionSuccessExpert(null);
                    setExpertForRequest(connectionSuccessExpert);
                }}
                onOpenChat={() => {
                    setConnectionSuccessExpert(null);
                    handleStartChat(connectionSuccessExpert);
                }}
            />
        )}
        <Snackbar
            open={!!successMessage || !!error}
            autoHideDuration={6000}
            onClose={() => { setSuccessMessage(null); setError(null); }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Alert onClose={() => { setSuccessMessage(null); setError(null); }} severity={successMessage ? 'success' : 'error'} sx={{ width: '100%' }}>
                {successMessage || error}
            </Alert>
        </Snackbar>
    </Box>
  );
};

interface MyCollaborationsTabContentProps {
    experts: ExpertListItemDto[];
    loading: boolean;
}

const MyCollaborationsTabContent: React.FC<MyCollaborationsTabContentProps> = ({ experts: myExperts, loading }) => {
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [expertForRequest, setExpertForRequest] = useState<ExpertListItemDto | null>(null);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleRequestConsultation = (expert: ExpertListItemDto) => {
        setExpertForRequest(expert);
    };

    const handleStartChat = async (expert: ExpertListItemDto) => {
        try {
            await dispatch(createConversation({ targetUserId: expert.id })).unwrap();
            navigate('/chat');
        } catch (err: any) {
            const errorMessage = err || 'Не вдалося почати чат. Спробуйте ще раз.';
            setError(errorMessage);
            console.error('Failed to start chat:', err);
        }
    };

    const handleCloseRequestModal = (isSuccess: boolean) => {
        setExpertForRequest(null);
        if (isSuccess) {
            setSuccessMessage('Ваш запис успішно створено! Очікуйте на підтвердження від експерта.');
        }
    };

    const activeExperts = myExperts.filter(e => e.interactionStatus === 'active');
    const pendingExperts = myExperts.filter(e => e.interactionStatus === 'pending');

    const renderExpertList = (
        experts: ExpertListItemDto[],
        showChatButton: boolean
    ) => {
        if (experts.length === 0) {
            return null;
        }

        return (
            <Grid container spacing={3} sx={{ mt: 2 }}>
                {experts.map(expert => (
                    <Grid item key={expert.id} xs={12} sm={12} md={6} lg={4}>
                        <ExpertCard
                            expert={expert}
                            onRequestConsultation={handleRequestConsultation}
                            onStartChat={handleStartChat}
                            showChatButton={showChatButton}
                        />
                    </Grid>
                ))}
            </Grid>
        );
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
    }
    
    if (!loading && myExperts.length === 0) {
        return <Typography sx={{ mt: 4, textAlign: 'center' }}>Ви ще не взаємодіяли з експертами.</Typography>;
    }

    return (
        <Box>
            {/* Active Experts Section (no title) */}
            {activeExperts.length > 0 && (
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    {activeExperts.map(expert => (
                        <Grid item key={expert.id} xs={12} sm={12} md={6} lg={4}>
                            <ExpertCard
                                expert={expert}
                                onRequestConsultation={handleRequestConsultation}
                                onStartChat={handleStartChat}
                                showChatButton={true}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Pending Experts Section */}
            {pendingExperts.length > 0 && (
                <Box mt={4}>
                    <Divider sx={{ mb: 4 }} />
                    <Typography variant="h5">В очікуванні</Typography>
                    <Typography paragraph sx={{mt: 1}}>Запити, що очікують підтвердження від експерта.</Typography>
                    {renderExpertList(pendingExperts, true)}
                </Box>
            )}

            {expertForRequest && (
                <ConsultationRequestModal
                    expert={expertForRequest}
                    open={!!expertForRequest}
                    onClose={handleCloseRequestModal}
                />
            )}
            <Snackbar
                open={!!successMessage || !!error}
                autoHideDuration={6000}
                onClose={() => { setSuccessMessage(null); setError(null); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => { setSuccessMessage(null); setError(null); }}
                    severity={successMessage ? "success" : "error"}
                    sx={{ width: '100%' }}
                >
                    {successMessage || error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

interface RejectedExpertsTabContentProps {
    experts: ExpertListItemDto[];
    loading: boolean;
    onExpertRemoved: (id: number) => void;
}

const RejectedExpertsTabContent: React.FC<RejectedExpertsTabContentProps> = ({ experts, loading, onExpertRemoved }) => {
    const [error, setError] = useState<string | null>(null);
    const [expertForConnection, setExpertForConnection] = useState<ExpertListItemDto | null>(null);
    const [connectionSuccessExpert, setConnectionSuccessExpert] = useState<ExpertListItemDto | null>(null);
    const [expertForConsultation, setExpertForConsultation] = useState<ExpertListItemDto | null>(null);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleRequestConnection = (expert: ExpertListItemDto) => {
        setExpertForConnection(expert);
    };

    const handleConnectionPermissionsConfirm = async (access: ConnectionAccessItem[], comment: string) => {
        if (!expertForConnection) return;
        const expert = expertForConnection;
        try {
            await requestConnection(expert.id, access, comment);
            onExpertRemoved(expert.id);
            setConnectionSuccessExpert(expert);
        } catch (err: any) {
            const msg = err.response?.data?.message;
            setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Не вдалося надіслати заявку.');
        } finally {
            setExpertForConnection(null);
        }
    };

    const handleStartChat = async (expert: ExpertListItemDto) => {
        try {
            await dispatch(createConversation({ targetUserId: expert.id })).unwrap();
            navigate('/chat');
        } catch (err: any) {
            setError(err || 'Не вдалося почати чат. Спробуйте ще раз.');
        }
    };

    if (loading) return <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

    return (
        <Box>
            {experts.length === 0 ? (
                <Typography sx={{ mt: 4, textAlign: 'center' }}>Відхилених заявок немає.</Typography>
            ) : (
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    {experts.map(expert => (
                        <Grid item key={expert.id} xs={12} sm={12} md={6} lg={4}>
                            <ExpertCard
                                expert={expert}
                                onRequestConsultation={() => {}}
                                onRequestConnection={handleRequestConnection}
                                onStartChat={() => {}}
                                showChatButton={false}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}
            {expertForConnection && (
                <ConnectionPermissionsDialog
                    open={!!expertForConnection}
                    onClose={() => setExpertForConnection(null)}
                    expertId={expertForConnection.id}
                    expertName={expertForConnection.name}
                    mode="new"
                    onConfirm={handleConnectionPermissionsConfirm}
                />
            )}
            {connectionSuccessExpert && (
                <ConnectionSuccessDialog
                    open={!!connectionSuccessExpert}
                    expertName={connectionSuccessExpert.name}
                    onClose={() => setConnectionSuccessExpert(null)}
                    onBookAppointment={() => {
                        const expert = connectionSuccessExpert;
                        setConnectionSuccessExpert(null);
                        setExpertForConsultation(expert);
                    }}
                    onOpenChat={() => {
                        setConnectionSuccessExpert(null);
                        handleStartChat(connectionSuccessExpert);
                    }}
                />
            )}
            {expertForConsultation && (
                <ConsultationRequestModal
                    expert={expertForConsultation}
                    open={!!expertForConsultation}
                    onClose={() => setExpertForConsultation(null)}
                />
            )}
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

const ExpertsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [allMyExperts, setAllMyExperts] = useState<ExpertListItemDto[]>([]);
  const [expertsLoading, setExpertsLoading] = useState(true);

  const fetchAllMyExperts = useCallback(async () => {
    try {
      setExpertsLoading(true);
      const response = await getMyExperts();
      setAllMyExperts(response.data);
    } catch (err) {
      console.error('Failed to fetch my experts', err);
    } finally {
      setExpertsLoading(false);
    }
  }, []);

  useEffect(() => { fetchAllMyExperts(); }, [fetchAllMyExperts]);

  const myExperts = allMyExperts.filter(e => e.interactionStatus === 'active' || e.interactionStatus === 'pending');
  const rejectedExperts = allMyExperts.filter(e => e.interactionStatus === 'rejected');

  const handleExpertRemoved = (id: number) => {
    setAllMyExperts(prev => prev.filter(e => e.id !== id));
  };

  const tabLabel = (label: string, count: number) =>
    count > 0 ? `${label} (${count})` : label;

  const tabs = [
    { value: 'find',     label: 'Знайти експерта' },
    { value: 'my',       label: tabLabel('Мої експерти', myExperts.length) },
    { value: 'rejected', label: tabLabel('Відхилені заявки', rejectedExperts.length) },
  ];

  const tabParam = searchParams.get('tab');
  const currentTab = tabs.some(t => t.value === tabParam) ? tabParam! : 'find';

  return (
    <DashboardLayout data-i-doc={PageIndices.EXPERTS}>
      <RoleProtectedRoute allowedRoles={['client']}>
        <Box sx={{ width: '100%' }}>
          <AdaptiveTabs tabs={tabs} searchParamKey="tab" defaultValue="find" />
          <Box sx={{ p: 3 }}>
            {currentTab === 'find' && (
              <Box data-i-doc={TabIndices.EXPERTS_FIND}>
                <PageHint iDoc={TabIndices.EXPERTS_FIND} />
                <FindExpertsTabContent />
              </Box>
            )}
            {currentTab === 'my' && (
              <Box data-i-doc={TabIndices.EXPERTS_MY}>
                <PageHint iDoc={TabIndices.EXPERTS_MY} />
                <MyCollaborationsTabContent experts={myExperts} loading={expertsLoading} />
              </Box>
            )}
            {currentTab === 'rejected' && (
              <Box data-i-doc={TabIndices.EXPERTS_REJECTED}>
                <PageHint iDoc={TabIndices.EXPERTS_REJECTED} />
                <RejectedExpertsTabContent experts={rejectedExperts} loading={expertsLoading} onExpertRemoved={handleExpertRemoved} />
              </Box>
            )}
          </Box>
        </Box>
      </RoleProtectedRoute>
    </DashboardLayout>
  );
};

export default ExpertsPage;
